import { useCallback } from 'react';

const DB_NAME = 'quanto-respondent-db';
const DB_VERSION = 1;
const STORE_NAME = 'respondent-data';

let dbPromise = null;

const openDB = () => {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: 'key',
        });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });

  return dbPromise;
};

const useIndexedDB = () => {
  const getData = useCallback(async (key) => {
    try {
      const db = await openDB();

      return await new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);

        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result?.value ?? null);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('IndexedDB getData error:', error);
      return null;
    }
  }, []);

  const setData = useCallback(async (key, value) => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      store.put({
        key,
        value,
        updatedAt: Date.now(),
      });

      transaction.oncomplete = () => {
        resolve(value);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  }, []);

  const removeData = useCallback(async (key) => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      store.delete(key);

      transaction.oncomplete = () => {
        resolve(true);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  }, []);

  const clearData = useCallback(async () => {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      store.clear();

      transaction.oncomplete = () => {
        resolve(true);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  }, []);

  return {
    getData,
    setData,
    removeData,
    clearData,
  };
};

export default useIndexedDB;
